import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  getIntegrationFields,
  updateIntegrationFields,
  UpdateIntegrationFieldsRequest,
  UpdateIntegrationFieldsResponse,
  deleteIntegrationFields,
  DeleteIntegrationFieldsRequest,
  DeleteIntegrationFieldsResponse,
  validateProcessedField,
  ValidationResponse,
} from '../integrations2.0';

export const useIntegrationFields = (
  integrationKey: string,
  object: string,
  externalObjectKey: string,
  auth0: any,
) => {
  const queryClient = useQueryClient();
  // bagel-admin is cross-tenant: accountId comes from the /account/:accountId route.
  // Field CRUD now targets the admin /admin/object-fields endpoints.
  const { accountId = '' } = useParams<{ accountId: string }>();

  const fieldsQuery = useQuery({
    queryKey: ['integrationFields', accountId, integrationKey, object, externalObjectKey],
    queryFn: () => getIntegrationFields(accountId, integrationKey, object, externalObjectKey),
    enabled: !!integrationKey && !!accountId,
  });

  const updateFieldMutation = useMutation({
    mutationFn: (fieldData: UpdateIntegrationFieldsRequest) =>
      updateIntegrationFields(accountId, integrationKey, object, externalObjectKey, fieldData),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['integration', integrationKey] });
      queryClient.invalidateQueries({ queryKey: ['integrationFields', accountId, integrationKey] });
    },
  });

  const blacklistFieldMutation = useMutation({
    mutationFn: (blacklistField: { action: 'add' | 'remove'; field: string }) =>
      updateIntegrationFields(accountId, integrationKey, object, externalObjectKey, {
        blacklistField,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', integrationKey] });
      queryClient.invalidateQueries({ queryKey: ['integrationFields', accountId, integrationKey] });
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (fieldData: DeleteIntegrationFieldsRequest) =>
      deleteIntegrationFields(accountId, integrationKey, object, externalObjectKey, fieldData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', integrationKey] });
      queryClient.invalidateQueries({ queryKey: ['integrationFields', accountId, integrationKey] });
    },
  });

  const validateFieldMutation = useMutation({
    mutationFn: ({ fieldName, sampleCount }: { fieldName: string; sampleCount: number }) =>
      validateProcessedField(
        integrationKey,
        object,
        externalObjectKey,
        fieldName,
        sampleCount,
        auth0,
      ),
  });

  return {
    fields: fieldsQuery.data?.fields || [],
    blacklistedFields: fieldsQuery.data?.blacklistedFields || [],
    isLoading: fieldsQuery.isLoading,
    isError: fieldsQuery.isError,
    error: fieldsQuery.error,
    updateField: updateFieldMutation.mutate,
    updateFieldAsync: updateFieldMutation.mutateAsync,
    isUpdating: updateFieldMutation.isPending,
    updateError: updateFieldMutation.error,
    addOrRemoveBlacklistedField: blacklistFieldMutation.mutate,
    addOrRemoveBlacklistedFieldAsync: blacklistFieldMutation.mutateAsync,
    isBlacklisting: blacklistFieldMutation.isPending,
    blacklistError: blacklistFieldMutation.error,
    deleteField: deleteFieldMutation.mutate,
    deleteFieldAsync: deleteFieldMutation.mutateAsync,
    isDeleting: deleteFieldMutation.isPending,
    deleteError: deleteFieldMutation.error,
    validateField: validateFieldMutation.mutateAsync,
    isValidating: validateFieldMutation.isPending,
    validateError: validateFieldMutation.error,
  };
};
